// slices/dataSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  APP_KEY,
  BACKOFFICE_URI,
  CAMPAINGS_URI,
  HIERARCHY_URI,
  HOST,
  ITEM_URI,
  LIST_URI,
  MANAGE_URI,
  OFFERS_URI,
  STORE_URI,
  ZONES_URI
} from 'variables';

const GET_ZONES_URL = HOST + BACKOFFICE_URI + ZONES_URI;
const GET_STORES_URL = HOST + '/backoffice/store/list';
const GET_HIERARCHIES_URL = HOST + BACKOFFICE_URI + ITEM_URI + HIERARCHY_URI;
const GET_CAMPAIGNS_URL = HOST + OFFERS_URI + MANAGE_URI + CAMPAINGS_URI;
// const GET_EVENTS_URL = HOST + `/get/info/mdm/${APP_KEY}/event_list/i`;
const GET_EVENTS_URL = HOST + `/get/info/a/${APP_KEY}/events`;
const GET_FEATURES_URL = HOST + `/get/info/a/${APP_KEY}/features`;
const GET_PROGRAM_TYPES_URL = HOST + `fin/programs/get/${APP_KEY}/program_types`;

// Define async thunks for each API request
export const getZones = createAsyncThunk('dropdowns/getZones', async ({ headers }) => {
  const response = await axios.get(GET_ZONES_URL, {
    params: { action: 'dd' },
    headers: headers
  });

  const data = await response.data;

  if (!data) return [];

  return data;
});

export const getStores = createAsyncThunk('dropdowns/getStores', async ({ headers }) => {
  const response = await axios.get(GET_STORES_URL, {
    params: { action: 'dd' },
    headers: headers
  });

  const data = await response.data.stores;

  if (!data) return [];

  return data;
});

export const getHierarchies = createAsyncThunk('dropdowns/getHierarchies', async ({ headers, params }) => {
  const response = await axios.get(GET_HIERARCHIES_URL, {
    params: { action: 'dd', ...params },
    headers: headers
  });

  const data = await response.data;

  if (!data) return [];

  return data;
});

export const getCampaigns = createAsyncThunk('dropdowns/getCampaigns', async ({ headers }) => {
  const response = await axios.get(GET_CAMPAIGNS_URL, {
    // params: { action: 'dd' },
    headers: headers
  });

  const data = await response.data;

  if (!data) return [];

  return data;
});

export const getFeatures = createAsyncThunk('dropdowns/getFeatures', async ({ headers }) => {
  const response = await axios.get(GET_FEATURES_URL, {
    headers
  });
  const data = await response.data;
  return data;
  // const formattedData = data?.map((feature) => ({ label: feature.feature_name, value: feature_code })) ?? [];
  // return formattedData;
});

export const getEvents = createAsyncThunk('dropdowns/getEvents', async ({ headers }) => {
  const response = await axios.get(GET_EVENTS_URL, {
    headers
  });
  const data = await response.data;

  return data;
});

export const getProgramTypes = createAsyncThunk('dropdowns/getProgramTypes', async ({ headers }) => {
  const response = await axios.get(GET_PROGRAM_TYPES_URL, {
    headers
  });
  const data = await response.data;

  const types = data.map((type) => {
    return type.type_name;
  });

  return types;
});

// export const getOrgCategories = createAsyncThunk('dropdowns/getOrgCategories', async () => {
//   const response = await axios.post(`${host}`, {
//     type: 'list',
//     attribute: 'ORG CATEGORY',
//     parent_value: '',
//     value: ''
//   });
//   const data = await response.data;

//   return data;
// });

// export const getAnswerTypes = createAsyncThunk('dropdowns/getAnswerTypes', async () => {
//   const response = await axios.post(`${host}`, {
//     type: 'list',
//     attribute: 'ANSWER TYPE',
//     parent_value: '',
//     value: ''
//   });
//   const data = await response.data;

//   return data;
// });

// Create a data slice
const dropdowns = createSlice({
  name: 'dropdowns',
  initialState: {
    zones: [],
    stores: [],
    hierarchies: [],
    campaigns: [],
    events: [],
    features: [],
    programTypes: [],
    // state: [],
    // countries: [],
    // orgCategories: [],
    // answerTypes: [],
    loading: false,
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Case for zones
      .addCase(getZones.pending, (state) => {
        state.loading = true;
      })
      .addCase(getZones.fulfilled, (state, action) => {
        state.loading = false;
        state.zones = action.payload;
      })
      .addCase(getZones.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Case for stores
      .addCase(getStores.pending, (state) => {
        state.loading = true;
      })
      .addCase(getStores.fulfilled, (state, action) => {
        state.loading = false;
        state.stores = action.payload;
      })
      .addCase(getStores.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Case for hierarchies
      .addCase(getHierarchies.pending, (state) => {
        state.loading = true;
      })
      .addCase(getHierarchies.fulfilled, (state, action) => {
        state.loading = false;
        state.hierarchies = action.payload;
      })
      .addCase(getHierarchies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Case for campaigns
      .addCase(getCampaigns.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload;
      })
      .addCase(getCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      //   // Case for State
      .addCase(getFeatures.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFeatures.fulfilled, (state, action) => {
        state.loading = false;
        state.features = action.payload;
      })
      .addCase(getFeatures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Case for events
      .addCase(getEvents.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload;
      })
      .addCase(getEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(getProgramTypes.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProgramTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.programTypes = action.payload;
      })
      .addCase(getProgramTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    //   // Case for zones
    //   .addCase(getOrgCategories.pending, (state) => {
    //     state.loading = true;
    //   })
    //   .addCase(getOrgCategories.fulfilled, (state, action) => {
    //     state.loading = false;
    //     state.orgCategories = action.payload;
    //   })
    //   .addCase(getOrgCategories.rejected, (state, action) => {
    //     state.loading = false;
    //     state.error = action.error.message;
    //   })

    //   // Case for zones
    //   .addCase(getAnswerTypes.pending, (state) => {
    //     state.loading = true;
    //   })
    //   .addCase(getAnswerTypes.fulfilled, (state, action) => {
    //     state.loading = false;
    //     state.answerTypes = action.payload;
    //   })
    //   .addCase(getAnswerTypes.rejected, (state, action) => {
    //     state.loading = false;
    //     state.error = action.error.message;
    //   });
  }
});

export const selectZones = (state) => state.dropdowns.zones;
export const selectStroes = (state) => state.dropdowns.stores;
export const selectHierarchies = (state) => state.dropdowns.hierarchies;
export const selectCampaigns = (state) => state.dropdowns.campaigns;
export const selectEvents = (state) => state.dropdowns.events;
export const selectFeatures = (state) => state.dropdowns.features;

export default dropdowns.reducer;
